import type {
  FlowNodeEntity,
  FreeLayoutPluginContext,
} from '@flowgram.ai/free-layout-editor';
import { WorkflowNodeLinesData } from '@flowgram.ai/free-layout-editor';
import type { FlowNodeData, SeedanceComplianceState } from '$/domains/workFlowV3/type';
import type { AtImageOption } from '$/components/InputTextAreaV2/types';
import { removeInputNodeInlineTokens } from '../../../utils/inputNodeInlineToken';
import type { MediaMiniPreviewType } from './MediaMiniPreview';

export { removeInputNodeInlineTokens };

export type InputNodePreviewType = 'text' | MediaMiniPreviewType;

export interface InputNodePreviewItem {
  id: string;
  type: InputNodePreviewType;
  url?: string;
  /** 视频压缩预览地址；MediaMiniPreview hover 播放优先使用。 */
  compressUrl?: string;
  coverUrl?: string;
  title?: string;
  seedanceCompliance?: SeedanceComplianceState;
  /** 当前目标节点校验该上游资源失败时携带的服务端提示，用于标记边框。 */
  validationError?: string;
}

export interface InputNodeMediaOrderItem {
  id: string;
  type: 'image' | 'video';
}

type InputNodeId = NonNullable<FlowNodeData['inputNodeId']>;
type InputNodeDocument = {
  getNode: (id: string) => FlowNodeEntity | undefined;
};

interface MediaLikeItem {
  name?: string;
  originUrl?: string;
  url?: string;
  cover_url?: string;
  coverUrl?: string;
  watermark_url?: string;
  watermark_cover_url?: string;
  compress_url?: string;
  compressUrl?: string;
  watermark_compress_url?: string;
  watermarkCompressUrl?: string;
  value?: string;
}

/**
 * 从节点 media_list 项解析视频压缩预览地址。
 * @param media 媒体项
 */
function pickMediaCompressUrl(media?: MediaLikeItem) {
  if (!media) return undefined;
  const compressUrl = (
    media.compress_url
    || media.compressUrl
    || media.watermark_compress_url
    || media.watermarkCompressUrl
    || ''
  ).trim();
  return compressUrl || undefined;
}

const EMPTY_INPUT_NODE_ID: InputNodeId = {
  textNodeIds: [],
  imageNodeIds: [],
  videoNodeIds: [],
  audioNodeIds: [],
};

const AT_REFERENCE_MEDIA_TYPES = new Set<InputNodePreviewItem['type']>(['image', 'video', 'audio']);

/** 底部媒体预览原生拖拽时写入 dataTransfer 的索引类型。 */
export const MEDIA_PREVIEW_DRAG_INDEX_MIME = 'application/x-roboneo-media-preview-index';

/**
 * 判断 inputNodeId 是否仍有任意输入节点。
 */
export function hasAnyInputNodeId(inputNodeId: InputNodeId) {
  return (
    inputNodeId.textNodeIds.length > 0
    || inputNodeId.imageNodeIds.length > 0
    || inputNodeId.videoNodeIds.length > 0
    || inputNodeId.audioNodeIds.length > 0
  );
}

/**
 * 从 inputNodeId 推导图片+视频的默认混合顺序（先图后视频）。
 */
export function resolveMediaPreviewOrder(inputNodeId?: FlowNodeId): string[] {
  if (!inputNodeId) return [];

  const imageNodeIds = inputNodeId.imageNodeIds ?? [];
  const videoNodeIds = inputNodeId.videoNodeIds ?? [];
  const allMediaIdSet = new Set([...imageNodeIds, ...videoNodeIds]);
  const savedOrder = (inputNodeId.mediaPreviewOrder ?? []).filter((id) => allMediaIdSet.has(id));
  const savedSet = new Set(savedOrder);
  const remainingIds = [
    ...imageNodeIds.filter((id) => !savedSet.has(id)),
    ...videoNodeIds.filter((id) => !savedSet.has(id)),
  ];

  return [...savedOrder, ...remainingIds];
}

/**
 * 将拖拽后的混合顺序写回 inputNodeId，并同步分类数组。
 */
export function syncInputNodeMediaOrder(
  inputNodeId: FlowNodeData['inputNodeId'],
  orderedMediaItems: InputNodeMediaOrderItem[],
): InputNodeId {
  const base = inputNodeId ?? EMPTY_INPUT_NODE_ID;
  const mediaPreviewOrder = orderedMediaItems.map((item) => item.id);

  return {
    textNodeIds: base.textNodeIds ?? [],
    imageNodeIds: orderedMediaItems.filter((item) => item.type === 'image').map((item) => item.id),
    videoNodeIds: orderedMediaItems.filter((item) => item.type === 'video').map((item) => item.id),
    audioNodeIds: base.audioNodeIds ?? [],
    mediaPreviewOrder,
  };
}

/**
 * 将预览项数组按 from/to 索引重排，供原生 DnD drop 后复用。
 */
export function reorderMediaPreviewItems<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex === toIndex
    || fromIndex < 0
    || toIndex < 0
    || fromIndex >= items.length
    || toIndex >= items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

/**
 * 将新连入的图片/视频节点 id 追加到 mediaPreviewOrder 末尾。
 */
export function appendMediaPreviewOrderId(
  inputNodeId: InputNodeId,
  nodeId: string,
  field: 'imageNodeIds' | 'videoNodeIds',
): InputNodeId {
  if (field !== 'imageNodeIds' && field !== 'videoNodeIds') {
    return inputNodeId;
  }

  const mediaPreviewOrder = resolveMediaPreviewOrder(inputNodeId);
  if (mediaPreviewOrder.includes(nodeId)) {
    return inputNodeId;
  }

  return {
    ...inputNodeId,
    mediaPreviewOrder: [...mediaPreviewOrder, nodeId],
  };
}

/**
 * 从 inputNodeId 中移除 mediaPreviewOrder 里的指定 id。
 */
export function removeMediaPreviewOrderIds(
  inputNodeId: InputNodeId,
  targetNodeIds: string[],
): InputNodeId {
  const targetSet = new Set(targetNodeIds);
  const nextOrder = (inputNodeId.mediaPreviewOrder ?? []).filter((id) => !targetSet.has(id));

  if (!nextOrder.length) {
    const { mediaPreviewOrder: _removed, ...rest } = inputNodeId;
    return rest as InputNodeId;
  }

  return {
    ...inputNodeId,
    mediaPreviewOrder: nextOrder,
  };
}

/**
 * 从输入节点 data 中读取第一个媒体内容。
 */
function getFirstMediaItem(inputNode: FlowNodeEntity) {
  const inputData = inputNode.toJSON()?.data as FlowNodeData<MediaLikeItem> | undefined;
  return {
    inputData,
    media: inputData?.media_list?.[0],
  };
}

/**
 * 文本输入节点只在存在执行结果或用户输入内容时参与底部预览。
 */
function collectTextInputPreview(document: InputNodeDocument, inputNodeId?: FlowNodeData['inputNodeId']) {
  return (inputNodeId?.textNodeIds ?? []).reduce<InputNodePreviewItem[]>((items, id) => {
    const inputNode = document.getNode(id);
    if (!inputNode) return items;
    const { inputData } = getFirstMediaItem(inputNode);
    const hasTextContent = (inputData?.media_list ?? []).some((item) => String(item?.value ?? '').trim());
    if (!hasTextContent) return items;
    items.push({
      id,
      type: 'text',
      title: inputData?.name,
    });
    return items;
  }, []);
}

/**
 * 媒体输入节点只在存在可展示 URL 或封面 URL 时参与底部预览。
 */
function collectMediaInputPreview(
  document: InputNodeDocument,
  ids: string[],
  type: MediaMiniPreviewType,
) {
  return ids.reduce<InputNodePreviewItem[]>((items, id) => {
    const inputNode = document.getNode(id);
    if (!inputNode) return items;
    const { inputData, media } = getFirstMediaItem(inputNode);
    const previewUrl = media?.originUrl || media?.url || media?.watermark_url;
    const coverUrl = media?.cover_url || media?.coverUrl || media?.watermark_cover_url;
    const compressUrl = type === 'video' ? pickMediaCompressUrl(media) : undefined;
    if (!previewUrl && !coverUrl) return items;
    items.push({
      id,
      type,
      url: previewUrl,
      ...(compressUrl ? { compressUrl } : {}),
      coverUrl,
      title: media?.name || inputData?.name,
      seedanceCompliance: media?.seedanceCompliance,
    });
    return items;
  }, []);
}

/**
 * 按 mediaPreviewOrder 收集图片+视频预览项，支持混合顺序回显。
 */
function collectOrderedImageVideoPreview(
  document: InputNodeDocument,
  inputNodeId?: FlowNodeId,
) {
  if (!inputNodeId) return [];

  const imageNodeIdSet = new Set(inputNodeId.imageNodeIds ?? []);
  const videoNodeIdSet = new Set(inputNodeId.videoNodeIds ?? []);
  const mediaPreviewOrder = resolveMediaPreviewOrder(inputNodeId);

  return mediaPreviewOrder.reduce<InputNodePreviewItem[]>((items, id) => {
    if (imageNodeIdSet.has(id)) {
      return [...items, ...collectMediaInputPreview(document, [id], 'image')];
    }
    if (videoNodeIdSet.has(id)) {
      return [...items, ...collectMediaInputPreview(document, [id], 'video')];
    }
    return items;
  }, []);
}

/**
 * 统一从目标节点 inputNodeId 中收集可在底部输入框展示的上游节点内容。
 */
export function collectInputNodePreviewItems(
  document: InputNodeDocument,
  inputNodeId?: FlowNodeId,
  types: InputNodePreviewType[] = ['text', 'image', 'video', 'audio'],
) {
  const result: InputNodePreviewItem[] = [];
  if (types.includes('text')) {
    result.push(...collectTextInputPreview(document, inputNodeId));
  }
  if (types.includes('image') || types.includes('video')) {
    const orderedMediaItems = collectOrderedImageVideoPreview(document, inputNodeId);
    result.push(...orderedMediaItems.filter((item) => types.includes(item.type)));
  }
  if (types.includes('audio')) {
    result.push(...collectMediaInputPreview(document, inputNodeId?.audioNodeIds ?? [], 'audio'));
  }
  return result;
}

/**
 * 将上游输入预览项转换成输入框 @ 候选项，供提示词序列化与回显复用。
 */
export function buildInputNodeAtImageOption(item: InputNodePreviewItem): AtImageOption {
  const name = item.title || item.id;
  let mediaType: AtImageOption['image']['mediaType'] = 'file';
  let typeKeyword = '音频';
  if (item.type === 'image') {
    mediaType = 'image';
    typeKeyword = '图片';
  }
  if (item.type === 'video') {
    mediaType = 'video';
    typeKeyword = '视频';
  }

  return {
    id: item.id,
    name,
    keywords: [
      name,
      item.type,
      typeKeyword,
      '输入',
    ].filter(Boolean) as string[],
    image: {
      url: item.url || item.coverUrl || '',
      name,
      index: item.id,
      mediaType,
      source: 'input',
      coverUrl: item.coverUrl,
    },
  };
}

/**
 * 从上游预览项中过滤可作为 @ 引用的媒体项，文本节点只做预览不进 inline 资源。
 */
export function collectInputNodeAtImageOptions(items: InputNodePreviewItem[]) {
  return items
    .filter((item) => AT_REFERENCE_MEDIA_TYPES.has(item.type) && (item.url || item.coverUrl))
    .map(buildInputNodeAtImageOption);
}

/**
 * 从 inputNodeId 中移除指定上游节点 id，供底部预览删除和连线删除复用。
 */
export function removeInputNodeIds(
  inputNodeId: FlowNodeData['inputNodeId'],
  targetNodeIds: string[],
) {
  if (!inputNodeId) return undefined;

  const targetSet = new Set(targetNodeIds);
  const nextInputNodeId = removeMediaPreviewOrderIds({
    textNodeIds: (inputNodeId.textNodeIds ?? EMPTY_INPUT_NODE_ID.textNodeIds).filter((id) => !targetSet.has(id)),
    imageNodeIds: (inputNodeId.imageNodeIds ?? EMPTY_INPUT_NODE_ID.imageNodeIds).filter((id) => !targetSet.has(id)),
    videoNodeIds: (inputNodeId.videoNodeIds ?? EMPTY_INPUT_NODE_ID.videoNodeIds).filter((id) => !targetSet.has(id)),
    audioNodeIds: (inputNodeId.audioNodeIds ?? EMPTY_INPUT_NODE_ID.audioNodeIds).filter((id) => !targetSet.has(id)),
    mediaPreviewOrder: inputNodeId.mediaPreviewOrder,
  }, targetNodeIds);

  return hasAnyInputNodeId(nextInputNodeId) ? nextInputNodeId : undefined;
}

/**
 * 删除目标节点与指定上游节点之间的输入连线；返回是否实际断开过连线。
 */
export function disconnectInputNodeLines(
  ctx: FreeLayoutPluginContext,
  node: FlowNodeEntity,
  targetNodeIds: string[],
) {
  const targetSet = new Set(targetNodeIds);
  const inputLines = node.getData(WorkflowNodeLinesData)?.inputLines ?? [];
  let disconnected = false;

  inputLines.forEach((line) => {
    const fromNodeId = line?.fromPort?.node?.id || line?.info?.from;
    if (!fromNodeId || !targetSet.has(fromNodeId)) return;
    if (line.disposed || !ctx.document.linesManager.canRemove(line)) return;
    line.dispose();
    disconnected = true;
  });

  return disconnected;
}
